import { typeVar, typeRole, ROLE_MARKER } from './typeRoles';

// ----------------------------------------------------------------------

export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value) {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({ sm, md, lg }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------
// Dashboard-driven variants. Each value reads its /dashboard/typography role
// variable (theme/typeRoles.js) and falls back to the original static value, so
// public pages follow Marketing's published scale while admin screens — where
// the variables are never defined — render exactly as before. The responsive
// steps are fallbacks only; once the variable exists it already switches
// between mobile and desktop sizes itself.

function responsiveRoleSizes(role, { sm, md, lg }) {
  return {
    '@media (min-width:600px)': { fontSize: typeVar(role, 'size', pxToRem(sm)) },
    '@media (min-width:900px)': { fontSize: typeVar(role, 'size', pxToRem(md)) },
    '@media (min-width:1200px)': { fontSize: typeVar(role, 'size', pxToRem(lg)) },
  };
}

function roleVariant(role, { fontSize, lineHeight, fontWeight, textTransform = 'inherit', responsive }) {
  return {
    fontWeight: typeVar(role, 'weight', fontWeight),
    lineHeight: typeVar(role, 'line-height', lineHeight),
    fontSize: typeVar(role, 'size', pxToRem(fontSize)),
    textTransform: typeVar(role, 'transform', textTransform),
    [ROLE_MARKER]: role,
    ...(responsive ? responsiveRoleSizes(role, responsive) : {}),
  };
}

// ----------------------------------------------------------------------

const FONT_PRIMARY = 'Public Sans, sans-serif'; // Google Font
// const FONT_SECONDARY = 'CircularStd, sans-serif'; // Local Font

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: roleVariant('h1', { fontWeight: 800, lineHeight: 80 / 64, fontSize: 40, responsive: { sm: 52, md: 58, lg: 64 } }),
  h2: roleVariant('h2', { fontWeight: 800, lineHeight: 64 / 48, fontSize: 32, responsive: { sm: 40, md: 44, lg: 48 } }),
  h3: roleVariant('h3', { fontWeight: 700, lineHeight: 1.5, fontSize: 24, responsive: { sm: 26, md: 30, lg: 32 } }),
  h4: roleVariant('h4', { fontWeight: 700, lineHeight: 1.5, fontSize: 20, responsive: { sm: 20, md: 24, lg: 24 } }),
  h5: roleVariant('h5-h6', { fontWeight: 700, lineHeight: 1.5, fontSize: 18, responsive: { sm: 19, md: 20, lg: 20 } }),
  h6: roleVariant('h5-h6', { fontWeight: 700, lineHeight: 28 / 18, fontSize: 17, responsive: { sm: 18, md: 18, lg: 18 } }),
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: roleVariant('body', { fontWeight: 400, lineHeight: 1.5, fontSize: 17 }),
  body2: roleVariant('body-small', { fontWeight: 400, lineHeight: 22 / 14, fontSize: 15 }),
  caption: roleVariant('body-small', { fontWeight: 400, lineHeight: 1.5, fontSize: 12 }),
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    textTransform: 'uppercase',
  },
  button: roleVariant('button', { fontWeight: 700, lineHeight: 24 / 14, fontSize: 15, textTransform: 'capitalize' }),

  // Public-site roles with no standard MUI variant. Element mapping is in
  // overrides/Typography.js (variantMapping).
  heroHeadline: typeRole('hero-headline'),
  heroSubheadline: typeRole('hero-subheadline'),
  footerHeading: typeRole('footer-heading'),
  footerText: typeRole('footer-text'),
  footerLegal: typeRole('footer-legal'),
};

export default typography;
